import { useEffect, useState, useRef } from "react";
import { DateRange } from "react-day-picker";
import { SignedInUser, NestedUser } from "@revelate/types";
import { commissionAuthorization, getCalcUrl } from "@/lib/api";
import useSWR, { KeyedMutator } from "swr";
import { useSimulatedProgress } from "./useSimulatedProgress";

interface UseCommissionsStreamProps {
  currentUser: SignedInUser | null;
  dateRange?: DateRange;
  userIds?: string[];
  isProjected?: boolean;
  isEphemeral?: boolean;
  logResult?: boolean;
  enabled?: boolean;
}

interface CommissionsStreamState {
  data: NestedUser[]; // TODO: This type is wrong now
  error: string | null;
  isLoading: boolean;
  logs?: string[];
  progress: number;
}

const initialState: CommissionsStreamState = {
  data: [],
  error: null,
  isLoading: false,
  progress: 0,
};

export function useCommissionsStream({
  currentUser,
  dateRange,
  userIds,
  isProjected = false,
  logResult = false,
  enabled = true,
  isEphemeral = true, // Skip interacting with the database
}: UseCommissionsStreamProps) {
  const [streamState, setStreamState] = useState<CommissionsStreamState>(initialState);
  const [lastUrl, setLastUrl] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const {
    progress: simulatedProgress,
    forceComplete: forceCompleteSimulatedProgress,
    resetProgress: resetSimulatedProgress,
  } = useSimulatedProgress();

  const url = enabled
    ? getCalcUrl(currentUser, dateRange, userIds, isProjected, isEphemeral, logResult)
    : null;

  // Use SWR for caching but don't use its fetcher
  const { data: cachedData, mutate } = useSWR<NestedUser[]>(
    url,
    null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateOnMount: false,
      revalidateIfStale: false,
      dedupingInterval: 30000,
      refreshInterval: 0,
    }
  );

  useEffect(() => {
    // Reset if streaming is disabled
    if (!enabled) {
      setStreamState(initialState);
      return;
    }

    // If parameters haven't changed and we have cached data, reuse it
    if (cachedData && !logResult && url === lastUrl) {
      setStreamState({
        data: cachedData,
        error: null,
        isLoading: false,
        progress: 100,
      });
      return;
    }

    // If no URL can be built, set an error immediately
    if (!url) {
      setStreamState(prev => ({
        ...prev,
        error: "Invalid URL parameters",
        isLoading: false,
        progress: 0,
      }));
      return;
    }

    setLastUrl(url);
    let isSubscribed = true;

    cleanupPreviousRequest();

    const controller = new AbortController();
    abortControllerRef.current = controller;

    startFetchStream(url, controller, isSubscribed);

    return () => {
      isSubscribed = false;
      cleanupPreviousRequest();
    };
  }, [url, cachedData, enabled, logResult, mutate]);

  const cleanupPreviousRequest = () => {
    abortControllerRef.current?.abort();
  };

  const startFetchStream = async (url: string, controller: AbortController, isSubscribed: boolean) => {
    try {
      resetSimulatedProgress();

      setStreamState(prev => ({
        ...prev,
        isLoading: true,
        error: null,
      }));

      const response = await fetch(`${import.meta.env.VITE_API_URL}/${url}`, {
        signal: controller.signal,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: await commissionAuthorization(),
        },
      });

      if (!response.ok || !response.body) throw new Error(response.statusText || "No response body");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      if (cachedData && isSubscribed) {
        setStreamState(prev => ({
          ...prev,
          data: cachedData,
          isLoading: true,
        }));
      }

      while (isSubscribed && !controller.signal.aborted) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        tryParseAndUpdate(buffer, controller, isSubscribed, mutate);
      }

      finalizeStream(buffer, controller, isSubscribed, mutate);

    } catch (error) {
      if (error instanceof Error && error.name !== "AbortError" && isSubscribed) {
        setStreamState(prev => ({
          ...prev,
          error: error.message,
          isLoading: false,
          progress: 0,
        }));
      }
    }
  };

  const tryParseAndUpdate = (buffer: string, controller: AbortController, isSubscribed: boolean, mutate: KeyedMutator<NestedUser[]>) => {
    try {
      const result = JSON.parse(buffer);
      if (result?.data && isSubscribed && !controller.signal.aborted) {
        forceCompleteSimulatedProgress();

        setStreamState({
          data: result.data,
          logs: result.logs,
          isLoading: false,
          error: null,
          progress: 100,
        });

        // Update SWR cache
        mutate(result.data, false);
      }
    } catch {
      // buffer not yet complete
    }
  };

  const finalizeStream = (buffer: string, controller: AbortController, isSubscribed: boolean, mutate: KeyedMutator<NestedUser[]>) => {
    if (!isSubscribed || controller.signal.aborted) return;
    tryParseAndUpdate(buffer, controller, isSubscribed, mutate);
  };

  return { ...streamState, progress: simulatedProgress };
}
